<template>
  <b-modal
    header-close-content=""
    v-model="isOpen"
    @close="hideDialog"
    @cancel="hideDialog"
    :id="calcModalName"
    ref="modal"
    body-class="no-overflow"
    :title="$t('RenameCatalog')"
    @ok="changeCatalogName"
    data-cy="channel-creator-catalog-rename-modal">
    <div class="rename-catalog-dialog" v-if="catalog">
      <div>
        <div class="alert alert-danger" v-if="error">
          {{ $t("FailedToUpdate") }}
        </div>
        <div class="row" :class="{ 'mt-2': index > 0 }" v-for="(language, index) in contentLanguages.enabled" :key="language.languageId">
          <div class="col-lg-12" :class="{ 'pt-2': index > 0 }">
            {{ $t("name") }}
            <span v-if="contentLanguages.enabled.length > 1">[{{ language.languageCode }}]</span>
          </div>
          <div class="col-lg-12 mt-2">
            <text-box
              :validation-data="nameValidationFeedback[language.languageCode]"
              v-model="catalog[language.languageCode].name"
              :source="catalog"
              field="name"
              :language="language"
              data-cy="channel-creator-catalog-rename-name-txtbox">
            </text-box>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-lg-12 pt-2">
            {{ $t("category") }}
          </div>
          <div class="col-lg-12 mt-2">
            <single-value-text-box-with-fallback
              attributeId="categories"
              attributeType="catalogs"
              :enable-suggestions="true"
              :source="catalog"
              field="category"
              :validation-data="categoryValidationFeedback"
              :language="primaryLanguage"
              v-model="catalog[primaryLanguage.languageCode].category"
              data-cy="channel-creator-catalog-rename-category-txtbox"></single-value-text-box-with-fallback>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <spinner></spinner>
    </div>
    <template #modal-footer="{ ok, cancel }">
      <b-btn @click="ok" :disabled="isModelInValid" variant="primary">{{ $t("Save") }}</b-btn>
      <b-btn @click="cancel" data-cy="channel-creator-catalog-rename-cancel-btn">{{ $t("Cancel") }}</b-btn>
    </template>
  </b-modal>
</template>

<script>
import { mapActions, mapState } from "vuex";
import TextBox from "../../ContentEditor/components/TextBoxWithFallback.vue";
import Spinner from "../../common/components/Spinner.vue";
import SingleValueTextBoxWithFallback from "../../ContentEditor/components/SingleValueTextBoxWithFallback.vue";
import _ from "lodash";

export default {
  props: ["showDialog", "selectedId", "selectedCatalog", "modalName"],
  data() {
    return {
      catalog: null,
      error: false,
      isOpen: false
    };
  },

  computed: {
    ...mapState("metadata", ["contentLanguages"]),
    ...mapState("channelCreator", ["catalogs"]),
    nameValidationFeedback() {
      const validationFeedback = [];
      this.contentLanguages.enabled.forEach((lang) => {
        const isNameEmpty =
          this.catalog && this.catalog[lang.languageCode] && (this.catalog[lang.languageCode].name == null || this.catalog[lang.languageCode].name == "");
        const isDuplicateNameExists =
          this.catalog &&
          this.catalog[lang.languageCode] &&
          this.catalogs.filter(
            (x) =>
              x[lang.languageCode].name &&
              x[lang.languageCode].name != "" &&
              this.catalog[lang.languageCode].name &&
              this.catalog[lang.languageCode].name != "" &&
              x[lang.languageCode].name != null &&
              x[lang.languageCode].name.toLowerCase() == this.catalog[lang.languageCode].name.toLowerCase() &&
              x[lang.languageCode].catalogId != this.selectedId
          ).length > 0;
        const isNameLengthExceeds =
          this.catalog && this.catalog[lang.languageCode] && this.catalog[lang.languageCode].name && this.catalog[lang.languageCode].name.length > 256;

        if (isNameEmpty && lang.languageCode == this.languageCode) {
          validationFeedback[lang.languageCode] = {
            isValid: false,
            feedback: this.$t("validation.valueRequired", { field: "Value" }),
            type: "error"
          };
        } else {
          validationFeedback[lang.languageCode] =
            isDuplicateNameExists || isNameLengthExceeds
              ? isDuplicateNameExists
                ? {
                    isValid: false,
                    feedback: this.$t("validation.duplicateExists", {
                      field: "name"
                    }),
                    type: "error"
                  }
                : {
                    isValid: false,
                    feedback: this.$t("validation.lengthExceeds", {
                      field: "name"
                    }),
                    type: "error"
                  }
              : null;
        }
      });
      return validationFeedback;
    },
    categoryValidationFeedback() {
      const isCategoryEmpty =
        this.catalog && this.catalog[this.languageCode] && (this.catalog[this.languageCode].category == null || this.catalog[this.languageCode].category == "");
      const isCategoryLengthExceeds =
        this.catalog && this.catalog[this.languageCode] && (this.catalog[this.languageCode].category == null || this.catalog[this.languageCode].category == "");

      return isCategoryEmpty || isCategoryLengthExceeds
        ? isCategoryEmpty
          ? {
              isValid: false,
              feedback: this.$t("validation.valueRequired", { field: "Value" }),
              type: "error"
            }
          : {
              isValid: false,
              feedback: this.$t("validation.lengthExceeds", { field: "Value" }),
              type: "error"
            }
        : null;
    },
    primaryLanguage() {
      return this.contentLanguages.enabled[0];
    },
    languageCode() {
      return this.contentLanguages.enabled[0].languageCode;
    },
    isModelInValid() {
      return !this.contentLanguages.enabled.every((lang) => this.nameValidationFeedback[lang.languageCode] == null) || !!this.categoryValidationFeedback;
    },
    calcModalName() {
      return this.modalName ?? "rename-catalog-dialog";
    }
  },
  methods: {
    ...mapActions("channelCreator", ["renameCatalog"]),
    changeCatalogName: async function () {
      const me = this;
      me.isBusy = true;
      me.error = false;
      try {
        const result = await me.renameCatalog(me.catalog);
        me.error = false;
        me.$emit("renamed", result);
      } catch (error) {
        console.error("renameCatalog failed with exception ", error);
        me.error = true;
      } finally {
        me.isBusy = false;
        this.$emit("update:showDialog", false);
      }
    },
    hideDialog() {
      this.$emit("update:showDialog", false);
    }
  },

  components: {
    TextBox,
    Spinner,
    SingleValueTextBoxWithFallback
  },
  watch: {
    selectedCatalog: {
      immediate: true,
      handler(nv) {
        this.catalog = _.cloneDeep(nv);
      }
    },
    catalog: {
      deep: true,
      handler() {
        this.error = false;
      }
    },
    showDialog(nv) {
      this.isOpen = nv;
    }
  }
};
</script>
